import { CSSProperties, ReactNode, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import clsx from 'clsx';
import {
    MdChatBubbleOutline,
    MdClose,
    MdErrorOutline,
    MdMoreHoriz,
    MdPauseCircleOutline,
    MdScreenShare,
    MdVolumeUp,
} from 'react-icons/md';
import {
    participantColor,
    REMOTE_CONTROL_EXCLUDE_CLASS,
    type ISupportDiagnostics,
    type IVoiceVolume,
} from '@srm/screen-sharing/light';
import Menu from '@srm/ui/menu';
import { UiProvider } from '@srm/ui/provider';
import Tooltip, { TooltipGroup } from '@srm/ui/tooltip';
import type { IListElement } from '@srm/ui/types/IListElement';
import Button from '@srm/ui/button';
import SupportBar, { type ISupportBarSpeaking } from '@molecules/SupportBar';
import { useConsentArming } from '@molecules/SupportBar/useConsentArming';
import SupportActionFeed from '@molecules/SupportActionFeed';
import SupportFeedbackBar from '@molecules/SupportFeedbackBar';
import UserHoverCard from '@molecules/UserHoverCard/UserHoverCard';
import { useScreenShareContext } from '@providers/ScreenShareProvider/ScreenShareContext';
import { announceSupport } from '@providers/ScreenShareProvider/supportAnnouncer';
import { SUPPORT_CHAT_TOGGLE_ATTRIBUTE } from '@providers/ScreenShareProvider/supportChat';
import { supportPopupRoot } from '@providers/ScreenShareProvider/supportPresence';
import { micFailureHint } from '@providers/ScreenShareProvider/supportVoice';
import type { IScreenShareParticipant } from '@srm/screen-sharing';
import {
    participantUserId,
    qualityText,
    resolveScreenShareBar,
    speakingText,
    suspendedDeadline,
} from './screenShareBarState';
import SupportMicButton from './parts/SupportMicButton';
import SupportDiagnosticsPreview from './parts/SupportDiagnosticsPreview';

export interface IScreenShareBarProps {
    /**
     * Полоса без места в оболочке (провайдер рисует её сам, пока `LayoutMain` не смонтирован): прибита к верху окна
     * и не занимает место оболочки.
     */
    floating?: boolean;
    /** Лента действий раскрыта с самого начала (витрина) */
    defaultFeedOpened?: boolean;
}

/** Имя сотрудника в заголовке полосы — с его карточкой по наведению, когда известен его пользователь. */
const titleWithCard = (title: string, person: string | null | undefined, userKey: string | undefined): ReactNode => {
    const at = person && userKey ? title.lastIndexOf(person) : -1;
    if (!person || !userKey || at < 0) return title;
    return (
        <>
            {title.slice(0, at)}
            <UserHoverCard userKey={userKey} fallbackName={person} position="bottom" inline>
                {person}
            </UserHoverCard>
            {title.slice(at + person.length)}
        </>
    );
};

/** Фокус пропал вместе с нажатой кнопкой плашки — на «Чат», а не в `body`. */
const focusChatToggle = () => {
    if (!document.activeElement || document.activeElement === document.body)
        document.querySelector<HTMLElement>(`[${SUPPORT_CHAT_TOGGLE_ATTRIBUTE}]`)?.focus();
};

/**
 * «Козодоев говорит» с эквалайзером его цвета (§31) — из своего стора: меняется несколько раз в секунду, а полоса на
 * это не перерисовывается.
 */
const SpeakingNote = ({
    participants,
    speaking,
}: {
    participants: readonly IScreenShareParticipant[];
    speaking: ISupportBarSpeaking;
}) => {
    const keys = useSyncExternalStore(speaking.subscribe, speaking.get);
    const talking = speakingText(participants, keys);
    if (!talking) return null;
    const speaker = participants.find((person) => keys.includes(person.peerId));
    return (
        <Tooltip wrapTarget={false} interactive={false} label={talking}>
            <span
                className="support-bar__speaking"
                data-testid="screen-share-speaking"
                style={
                    {
                        '--support-bar-speaker': speaker ? participantColor(speaker.color) : undefined,
                    } as CSSProperties
                }
            >
                <span className="support-bar__equalizer" aria-hidden>
                    <i />
                    <i />
                    <i />
                </span>
                <span className="support-bar__speaking-text">{talking}</span>
            </span>
        </Tooltip>
    );
};

/**
 * Полоса показа экрана у пользователя: состояние сеанса и его кнопки. Место — над страницей в оболочке. Всплывающие
 * части (меню, подсказки, «Посмотреть, что уйдёт») — в хосте порталов слоя оверлеев поддержки: при ограниченном
 * захвате (Element Capture) сотрудник их не видит.
 */
const ScreenShareBar = ({ floating = false, defaultFeedOpened = false }: IScreenShareBarProps) => {
    const share = useScreenShareContext();
    const { registerBarSlot } = share;
    useEffect(() => (floating ? undefined : registerBarSlot()), [floating, registerBarSlot]);
    const [popupHost] = useState(supportPopupRoot);

    const [feedOpened, setFeedOpened] = useState(defaultFeedOpened);
    const count = share.actions.length;
    useEffect(() => {
        if (!share.active) setFeedOpened(false);
    }, [share.active]);

    const state = resolveScreenShareBar(share);
    // Диктору — через постоянную live-область провайдера: своя область полосы монтируется уже с текстом.
    const spoken = state ? [state.title, state.description].filter(Boolean).join(', ') : '';
    useEffect(() => {
        if (spoken) announceSupport(spoken);
    }, [spoken]);
    // Лента сама не раскрывается: она над страницей и попадает в показ — закрывала бы сотруднику вид. Последнее
    // действие в управлении — строкой полосы вместо «ваш ввод приостановлен» (оно — в подсказке) и диктору.
    const lastAction = state?.kind === 'control' && !state.busy ? share.actions.at(-1)?.text : undefined;
    useEffect(() => {
        if (lastAction) announceSupport(lastAction);
    }, [lastAction, count]);
    const micHint = micFailureHint(share.voice);
    useEffect(() => {
        if (micHint) announceSupport(micHint);
    }, [micHint]);
    // «Продолжить показ» пропадает вместе с паузой: фокус с неё — на «Чат», а не в `body`.
    const paused = state?.kind === 'suspended';
    const wasPaused = useRef(paused);
    useEffect(() => {
        const resumed = wasPaused.current && !paused && share.active;
        wasPaused.current = paused;
        if (resumed) focusChatToggle();
    }, [paused, share.active]);
    // Плашка жеста или диагностики ушла вместе с нажатой кнопкой («Отправить», «Не отправлять», действие) — так же.
    const gestureShown = !!(share.active && share.gesture);
    const diagnosticsShown = !!(share.active && share.diagnostics);
    const plaques = useRef({ gestureShown, diagnosticsShown });
    useEffect(() => {
        const previous = plaques.current;
        plaques.current = { gestureShown, diagnosticsShown };
        const answered = (previous.gestureShown && !gestureShown) || (previous.diagnosticsShown && !diagnosticsShown);
        if (answered && share.active) focusChatToggle();
    }, [gestureShown, diagnosticsShown, share.active]);

    // Кнопки согласия принимают нажатие не сразу после появления или сдвига (§40): момент запроса выбирает сотрудник.
    const gestureArming = useConsentArming(gestureShown ? share.gesture : null);
    const diagnosticsArming = useConsentArming(diagnosticsShown ? share.diagnostics?.requestId : null);
    const controlArming = useConsentArming(
        state?.kind === 'requested' ? `${share.control.peerId ?? ''}:${share.control.allowing ?? ''}` : null,
    );

    // Команда сотрудника, которую браузер выполнит только по нажатию пользователя (§40): нажатие кнопки — тот самый жест.
    const asking = share.control.peerName || share.viewerName;
    const askingKey = participantUserId(share.participants, asking);
    const gesture = share.active && share.gesture && (
        <SupportBar
            tone="control"
            person={asking}
            personKey={askingKey}
            title={titleWithCard(`${asking || 'Сотрудник поддержки'} просит`, asking, askingKey)}
            description={share.gesture.text}
            label="Просьба сотрудника поддержки"
            live="alert"
            floating={floating}
            data-testid="screen-share-gesture"
            actions={
                <>
                    <Button
                        size="sm"
                        ref={gestureArming.ref}
                        data-arming={!gestureArming.armed || undefined}
                        data-testid="screen-share-gesture-run"
                        onClick={gestureArming.guard(share.runGesture)}
                    >
                        {share.gesture.action}
                    </Button>
                    <Button size="sm" variant="outline-neutral" onClick={share.dismissGesture}>
                        Отклонить
                    </Button>
                </>
            }
        />
    );
    // Запрос технических сведений (§43): согласие — «Отправить»; «Посмотреть, что уйдёт» раскрывает пакет, и уходит
    // ровно он. Плашка — в полосе (её видно в показе), вне удалённого управления: нажать «Отправить» может только сам
    // пользователь; раскрытый пакет — в слое оверлеев, вне захвата (при Element Capture сотрудник его не видит).
    const ask = share.active ? share.diagnostics : null;
    const [preview, setPreview] = useState<{ requestId: string; diagnostics: ISupportDiagnostics } | null>(null);
    const [previewAnchor, setPreviewAnchor] = useState<HTMLElement | null>(null);
    const shownPreview = ask && preview?.requestId === ask.requestId ? preview.diagnostics : null;
    const askingDiagnostics = ask?.actorName || 'Сотрудник поддержки';
    const askingDiagnosticsKey = participantUserId(share.participants, ask?.actorName);
    const sending = ask?.phase === 'sending';
    const diagnostics = ask && (
        <SupportBar
            tone="view"
            person={ask.actorName || null}
            personKey={askingDiagnosticsKey}
            title={titleWithCard(
                `${askingDiagnostics} просит собрать диагностику`,
                ask.actorName,
                askingDiagnosticsKey,
            )}
            description={
                ask.phase === 'failed'
                    ? ask.error
                    : 'Отправить технические сведения? Окружение, версия, открытый документ без значений полей, последние запросы и ошибки'
            }
            label="Запрос технических сведений"
            live="alert"
            wrap
            floating={floating}
            data-testid="screen-share-diagnostics"
            actions={
                <>
                    <Button
                        size="sm"
                        variant="outline-neutral"
                        aria-expanded={!!shownPreview}
                        data-testid="screen-share-diagnostics-preview-toggle"
                        onClick={() => {
                            if (shownPreview) {
                                setPreview(null);
                                return;
                            }
                            const collected = share.previewDiagnostics();
                            if (collected) setPreview({ requestId: ask.requestId, diagnostics: collected });
                        }}
                    >
                        Посмотреть, что уйдёт
                    </Button>
                    <Button
                        size="sm"
                        loading={sending}
                        ref={diagnosticsArming.ref}
                        data-arming={!diagnosticsArming.armed || undefined}
                        data-testid="screen-share-diagnostics-send"
                        onClick={diagnosticsArming.guard(() => void share.sendDiagnostics(shownPreview ?? undefined))}
                    >
                        Отправить
                    </Button>
                    <Button
                        size="sm"
                        variant="outline-neutral"
                        disabled={sending}
                        data-testid="screen-share-diagnostics-decline"
                        onClick={share.declineDiagnostics}
                    >
                        Не отправлять
                    </Button>
                </>
            }
        >
            <span className="support-bar__preview-anchor" ref={setPreviewAnchor} aria-hidden />
            {shownPreview && (
                <SupportDiagnosticsPreview
                    diagnostics={shownPreview}
                    anchor={previewAnchor}
                    visibleToSupport={!share.restricted || share.cropped}
                    onClose={() => setPreview(null)}
                />
            )}
        </SupportBar>
    );
    const feedback = !share.active && share.feedback && (
        <SupportFeedbackBar
            key={share.feedback.caseId}
            names={share.feedback.names}
            minutes={share.feedback.minutes}
            sent={share.feedback.sent}
            onSend={share.sendFeedback}
            onHold={share.holdFeedback}
            onDismiss={share.dismissFeedback}
            floating={floating}
        />
    );
    // Всплывающее полосы (меню, подсказки, карточки, предпросмотр) — в хост порталов слоя оверлеев.
    const withPopupHost = (content: ReactNode) => <UiProvider portalTarget={popupHost}>{content}</UiProvider>;
    if (!state) return feedback ? withPopupHost(feedback) : null;

    const live = state.kind === 'viewing' || state.kind === 'requested' || state.kind === 'control';
    const feedAvailable = share.active && (state.kind === 'control' || count > 0);
    const feedButton = feedAvailable && (
        <Button
            size="sm"
            variant="outline-neutral"
            aria-expanded={feedOpened}
            onClick={() => setFeedOpened((opened) => !opened)}
        >
            Действия · {count}
        </Button>
    );
    // Подписи, которые плашка укорачивает, когда ей не хватает места (container query в SupportBar.scss).
    const stop = (
        <Button size="sm" variant="outline-neutral" aria-label="Остановить показ" onClick={share.stop}>
            <span className="support-bar__stop-full">Остановить показ</span>
            <span className="support-bar__stop-short" aria-hidden>
                Остановить
            </span>
        </Button>
    );
    // Голос — когда экран смотрят (§31), и пока сотрудник просит управление; чат — с начала показа, и пока ждём
    // сотрудника (§30).
    const voiceShown =
        share.voice.unavailable !== 'disabled' &&
        (state.kind === 'viewing' ||
            state.kind === 'requested' ||
            state.kind === 'control' ||
            state.kind === 'peer-reconnecting');
    const micButton = voiceShown && (
        <SupportMicButton
            voice={share.voice}
            level={share.micLevel}
            sound={share.sound}
            onToggle={() => void share.toggleMic()}
        />
    );
    // Громкость сотрудника у себя (IMPLEMENTATION.md §42): нажатие на кружок — меню громкости, наведение — карточка.
    const voiceLive = voiceShown && share.voice.unavailable !== 'snapshots';
    const people = voiceLive
        ? state.people?.map((person) => {
              const userKey = person.userKey;
              return userKey
                  ? {
                        ...person,
                        volume: share.sound.volumeOf(userKey),
                        onVolumeChange: (change: Partial<IVoiceVolume>) => share.sound.setVolume(userKey, change),
                    }
                  : person;
          })
        : state.people;
    // Браузер не дал проиграть голос специалиста без нажатия — звук включается щелчком.
    const soundButton = voiceShown && share.voice.playbackBlocked && (
        <Button
            size="sm"
            variant="outline-neutral"
            startAdornment={<MdVolumeUp size={18} />}
            data-testid="screen-share-resume-voice"
            onClick={share.resumeVoice}
        >
            Включить звук
        </Button>
    );
    const unread = share.chat.unread;
    // Непрочитанные при свёрнутой панели: счётчик и мягкая пульсация кнопки, пока не прочитано.
    const unreadShown = unread > 0 && !share.chatOpened;
    const chatButton = state.kind !== 'error' && (
        <Button
            size="sm"
            variant="outline-neutral"
            className={clsx('support-bar__chat', unreadShown && 'support-bar__chat--unread')}
            startAdornment={<MdChatBubbleOutline size={18} />}
            endAdornment={
                unreadShown ? (
                    <span className="support-bar__count" aria-hidden data-testid="screen-share-chat-unread">
                        {unread > 99 ? '99+' : unread}
                    </span>
                ) : undefined
            }
            aria-expanded={share.chatOpened}
            aria-label={unreadShown ? `Чат, непрочитанных: ${unread}` : 'Чат'}
            data-testid="screen-share-chat"
            {...{ [SUPPORT_CHAT_TOGGLE_ATTRIBUTE]: '' }}
            onClick={() => share.setChatOpened(!share.chatOpened)}
        >
            <span className="support-bar__chat-label">Чат</span>
        </Button>
    );
    // Пометки сотрудников (IMPLEMENTATION.md §26) убираются у пользователя и у всех сотрудников сразу.
    const clearAnnotations = share.active && share.annotated && (
        <Button size="sm" variant="outline-neutral" onClick={share.clearAnnotations}>
            Убрать пометки
        </Button>
    );
    // «Запретить пометки» (§40): сотрудники не рисуют на экране, курсор и указка остаются. Меню — вне захвата и вне
    // глушения ввода: управление его не нажмёт, пользователь — сможет.
    const moreItems: IListElement[] = [
        {
            label: share.annotationsAllowed ? 'Запретить пометки' : 'Разрешить пометки',
            description: share.annotationsAllowed
                ? 'Сотрудники не смогут рисовать на экране — курсор и указка останутся'
                : 'Сотрудники снова смогут рисовать пометки',
            value: 'annotations',
            handler: () => share.setAnnotationsAllowed(!share.annotationsAllowed),
        },
    ];
    const moreButton = live && (
        <Tooltip label="Ещё: пометки сотрудников">
            <Menu list={moreItems} position="bottom-end" popoverProps={{ className: REMOTE_CONTROL_EXCLUDE_CLASS }}>
                <Button size="sm" variant="outline-neutral" aria-label="Ещё" data-testid="screen-share-more">
                    <MdMoreHoriz size={18} aria-hidden />
                </Button>
            </Menu>
        </Tooltip>
    );
    let actions = (
        <>
            {soundButton}
            {micButton}
            {chatButton}
            {clearAnnotations}
            {feedButton}
            {moreButton}
            {stop}
        </>
    );
    if (state.kind === 'requested')
        // Микрофон и чат остаются: включённый микрофон можно выключить, раскрытый чат не теряет кнопку (§30, §31).
        // «Ещё» и «Остановить показ» — на прежних местах справа: запрос не подставляет «Разрешить» под щелчок по ним.
        actions = (
            <>
                {micButton}
                {chatButton}
                <Button
                    size="sm"
                    loading={!!share.control.allowing}
                    ref={controlArming.ref}
                    data-arming={!controlArming.armed || undefined}
                    data-testid="screen-share-allow-control"
                    onClick={controlArming.guard(() => share.allowControl(share.control.peerId))}
                >
                    Разрешить
                </Button>
                <Button size="sm" variant="outline-neutral" onClick={share.revokeControl}>
                    Отклонить
                </Button>
                {moreButton}
                {stop}
            </>
        );
    else if (state.kind === 'control')
        actions = (
            <>
                {soundButton}
                {micButton}
                {chatButton}
                {clearAnnotations}
                {feedButton}
                {moreButton}
                <Button size="sm" onClick={share.revokeControl} aria-keyshortcuts="Escape">
                    Забрать управление<kbd aria-hidden>Esc</kbd>
                </Button>
            </>
        );
    else if (state.kind === 'suspended')
        // После обновления страницы (§35): захват — только по нажатию, переписка не прерывается.
        actions = (
            <>
                {chatButton}
                <Button
                    size="sm"
                    // Показ ещё возвращается после перезагрузки (клиент грузится): нажатию пока нечего продолжать.
                    loading={share.resuming || share.restoring}
                    startAdornment={<MdScreenShare size={18} />}
                    data-testid="screen-share-continue"
                    onClick={() => void share.continueShare()}
                >
                    Продолжить показ
                </Button>
                <Tooltip wrapTarget={false} label="Завершить показ">
                    <Button
                        size="sm"
                        variant="outline-neutral"
                        className="support-bar__end"
                        aria-label="Завершить показ"
                        onClick={share.stop}
                    >
                        <span className="support-bar__end-label">Завершить</span>
                        <MdClose className="support-bar__end-icon" size={18} aria-hidden />
                    </Button>
                </Tooltip>
            </>
        );
    else if (state.kind === 'error')
        actions = (
            <>
                {/* Выбрали не вкладку (§40): выбор заново — из этого нажатия, браузеру нужен жест. */}
                {share.reselect && (
                    <Button
                        size="sm"
                        startAdornment={<MdScreenShare size={18} />}
                        data-testid="screen-share-reselect"
                        onClick={() => void share.startAgain()}
                    >
                        Выбрать заново
                    </Button>
                )}
                <Button size="sm" variant="outline-neutral" onClick={share.dismissError}>
                    Закрыть
                </Button>
            </>
        );

    // Узкой плашке — только точка, текст в подсказке.
    const quality = live && share.quality && (
        <Tooltip wrapTarget={false} interactive={false} label={qualityText(share.quality, share.transport)}>
            <span className="support-bar__quality">
                <span className="support-bar__quality-dot" data-quality={share.quality} aria-hidden />
                <span
                    className="support-bar__quality-text"
                    data-testid="screen-share-quality"
                    data-transport={share.transport}
                >
                    {qualityText(share.quality, share.transport)}
                </span>
            </span>
        </Tooltip>
    );
    // Кто из специалистов говорит (§31): эквалайзер его цветом и подпись рядом с кольцом у кружка.
    const aside = live ? (
        <>
            <SpeakingNote participants={share.participants} speaking={share.speaking} />
            {quality}
        </>
    ) : undefined;

    return withPopupHost(
        // Подсказки кнопок и сведений полосы — одна группа: соседние не накладываются, следующая сменяет прежнюю.
        <TooltipGroup>
            <SupportBar
                tone={state.tone}
                person={state.person}
                personKey={state.personKey}
                people={people}
                speaking={share.speaking}
                busy={state.busy}
                icon={
                    state.kind === 'error' ? (
                        <MdErrorOutline size={20} />
                    ) : state.kind === 'suspended' ? (
                        <MdPauseCircleOutline size={20} />
                    ) : undefined
                }
                hint={
                    state.kind === 'suspended' || lastAction
                        ? [state.title, state.description, lastAction].filter(Boolean).join(' · ')
                        : state.hint
                }
                title={
                    state.kind === 'suspended' ? (
                        <>
                            <span className="support-bar__pause-full">{state.title}</span>
                            <span className="support-bar__pause-short">Показ приостановлен</span>
                        </>
                    ) : state.shortTitle ? (
                        <>
                            <span className="support-bar__title-full">{state.title}</span>
                            <span className="support-bar__title-short">{state.shortTitle}</span>
                        </>
                    ) : (
                        titleWithCard(state.title, state.person, state.personKey)
                    )
                }
                description={
                    state.kind === 'suspended' && !share.resumeError ? (
                        <span className="support-bar__pause-description">
                            <span className="support-bar__pause-full">{state.description}</span>
                            <span className="support-bar__pause-short">
                                {suspendedDeadline(share.connection?.suspendGraceSeconds) || state.description}
                            </span>
                        </span>
                    ) : lastAction ? (
                        lastAction
                    ) : (
                        state.description
                    )
                }
                aside={aside}
                actions={actions}
                label="Показ экрана поддержке"
                live={false}
                floating={floating}
                data-testid="screen-share-bar"
            >
                {feedAvailable && feedOpened && (
                    <SupportActionFeed entries={share.actions} onClose={() => setFeedOpened(false)} />
                )}
            </SupportBar>
            {gesture}
            {diagnostics}
            {feedback}
        </TooltipGroup>,
    );
};

export default ScreenShareBar;
